import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useIxfWatchlistV1Fincen314MatchQueryServiceFincen314MatchGet } from '@interxfi/api-client/generated/hooks/ixf-watchlist-v1-fincen314-match-query-service-fincen314-match-get';
import {
  EntityActionMenu,
  type EntityActionMenuConfig,
  type EntityActionMenuItem,
} from '@interxfi/app-components/components/entity-action-menu/entity-action-menu';
import { PageHeader } from '@interxfi/app-components/components/layout/page-header/page-header';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyList } from '@interxfi/app-components/components/readonly-list/app-readonly-list';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  LAYOUT_ZERO_CONTAINER_PADDING,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container';
import type { ReadonlyListItem } from '@interxfi/design-system/components/list';
import { Typography } from '@interxfi/design-system/components/typography';
import XLargeIcon from '@interxfi/design-system/icons/react/icon-x-large';
import { type IxfWatchlistV1Fincen314MatchState, IxfWatchlistV1Fincen314MatchStatus, IxfWatchlistV1ListType } from '@interxfi/types';
import { STABLE_EMPTY_ARRAY_REF } from '@interxfi/utilities/array/stable-empty';
import { FincenMatchEventDrawer, FincenMatchEventSummary } from '../../../../../../../../../../features/watchlist/fincen/match/fincen-match-event';
import { getFincenMatchStateListItems } from '../../../../../../../../../../features/watchlist/fincen/match/fincen-match-list-items';
import { getFincenMatchDetailPath } from '../../../../../../../../../../features/watchlist/fincen/match/fincen-match-routing';
import { FincenMatchSpecTable } from '../../../../../../../../../../features/watchlist/fincen/match/fincen-match-spec-table';
import { WatchlistFincenMatchStatusPill } from '../../../../../../../../../../features/watchlist/fincen/match/fincen-match-status-pill';
import { WatchlistMatchCloseConfirmDialog } from '../../../../../../../../../../features/watchlist/watchlist-match-close-confirm-dialog';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../../../../../util/routing/meta';

export const Route = createFileRoute('/_app/_clearing-core/_clearing-required/clearing/compliance/_compliance/fincen/match/_match/$fincen314MatchId')(
  {
    loader: ({ params }) =>
      ({
        breadcrumb: buildBreadcrumb({
          id: `fincen-match-${params.fincen314MatchId}`,
          label: 'Match',
          to: getFincenMatchDetailPath(params.fincen314MatchId),
          suffix: (
            <CopyButton
              content={params.fincen314MatchId}
              label="Copy FinCEN Match ID"
              tooltip={{ copy: params.fincen314MatchId, copied: 'Copied FinCEN Match ID' }}
            />
          ),
        }),
      }) satisfies AggregateBreadcrumb,
    head: () => getPageHead('FinCEN Match'),
    component: FincenMatchDetail,
  },
);

function getMenuItems(fincen314MatchId: string, status: IxfWatchlistV1Fincen314MatchStatus | undefined): EntityActionMenuConfig {
  return {
    eventDrawer: {
      type: 'drawer',
      drawer: {
        Component: FincenMatchEventDrawer,
        props: { request: { fincen314MatchId } },
      },
    },
    actions:
      status === IxfWatchlistV1Fincen314MatchStatus.Opened
        ? ([
            {
              id: 'close',
              icon: <XLargeIcon aria-hidden />,
              label: 'Close Match',
              tone: 'danger-dk',
              interaction: {
                type: 'modal',
                modal: {
                  Component: WatchlistMatchCloseConfirmDialog,
                  props: { matchId: fincen314MatchId, listType: IxfWatchlistV1ListType.Fincen },
                },
              },
              requiresWrite: true,
            },
          ] as const satisfies EntityActionMenuItem[])
        : undefined,
  };
}

function getListItems(data: IxfWatchlistV1Fincen314MatchState | undefined) {
  const items = getFincenMatchStateListItems(data);

  return {
    left: [items.profileId] as const satisfies ReadonlyListItem[],
    right: [items.metadata.createdAt, items.metadata.updatedAt, items.fincen314MatchId] as const satisfies ReadonlyListItem[],
  } as const;
}

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load FinCEN match', 'view this FinCEN match');

function FincenMatchDetail() {
  const { fincen314MatchId } = Route.useParams();
  const request = { fincen314MatchId } as const;
  const { data, error, isPending } = useIxfWatchlistV1Fincen314MatchQueryServiceFincen314MatchGet(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);

  const listItems = useMemo(() => getListItems(data?.fincen314Match), [data?.fincen314Match]);
  const menuItems = useMemo(() => getMenuItems(fincen314MatchId, data?.fincen314Match?.status), [fincen314MatchId, data?.fincen314Match?.status]);

  return (
    <PageLayout maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section noBorder>
        <PageHeader>
          <HorizontalStack gap={2} wrap="nowrap">
            <WatchlistFincenMatchStatusPill isPending={showPending} size="large" status={data?.fincen314Match?.status} />
          </HorizontalStack>

          <EntityActionMenu {...menuItems} isPending={showPending} />
        </PageHeader>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Events</Typography.Level3>

          <FincenMatchEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Data</Typography.Level3>

          <WrappingLayoutContainer
            gap={DEFAULT_PAGE_LAYOUT_GAP}
            left={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <AppReadonlyList isPending={showPending} items={listItems.left} />,
            }}
            padding={LAYOUT_ZERO_CONTAINER_PADDING}
            right={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <BoxedAppReadonlyList isPending={showPending} items={listItems.right} />,
            }}
            wrap="forward"
          />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Matched Entries</Typography.Level3>

          <FincenMatchSpecTable loadingRows={2} specs={data?.fincen314Match?.data?.spec ?? STABLE_EMPTY_ARRAY_REF} />
        </VerticalStack>
      </PageLayout.Section>
    </PageLayout>
  );
}
